// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import {
  Bold,
  Code,
  Italic,
  List,
  Quote,
  Strikethrough,
} from 'lucide-react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { log } from '../../log/log';
import { notesStrings } from './strings';
import type { Note, NoteId, NotesUpdater } from './types';

export interface NoteEditorProps {
  readonly note: Note;
  readonly onSave: NotesUpdater;
  readonly onClose?: (() => void) | undefined;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'unsaved';

/**
 * Plain-text note editor (v1). Auto-saves on blur via the host's `onSave`
 * updater; rich-text lands when the cycle-16 RichTextEditor is hooked
 * into a follow-up slice. Note bodies are sensitive — never serialised
 * into a logger argument (Hard rule 3).
 */
export const NoteEditor: React.FC<NoteEditorProps> = ({ note, onSave, onClose }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  // Tracks the body input's caret/selection so the markdown toolbar can
  // wrap the selected range (or insert at the caret). Bodies persist as
  // markdown — the toolbar is functional rich-text authoring, not chrome.
  const [selection, setSelection] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  useEffect(() => {
    setTitle(note.title);
    setBody(note.body);
    setSaveState('idle');
    setSelection({ start: 0, end: 0 });
  }, [note]);

  // Wrap the current selection with a markdown marker on both sides
  // (bold/italic/strike/code). With no selection, inserts an empty pair
  // and parks the caret between the markers.
  const wrapSelection = useCallback(
    (marker: string) => {
      setBody((prev) => {
        const { start, end } = selection;
        const next =
          prev.slice(0, start) + marker + prev.slice(start, end) + marker + prev.slice(end);
        return next;
      });
      setSelection((s) => ({
        start: s.start + marker.length,
        end: s.end + marker.length,
      }));
      setSaveState('unsaved');
    },
    [selection],
  );

  // Prefix the line(s) intersecting the selection with a markdown line
  // marker (bullet "- " / quote "> ").
  const prefixLine = useCallback(
    (prefix: string) => {
      setBody((prev) => {
        const { start } = selection;
        const lineStart = prev.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
        return prev.slice(0, lineStart) + prefix + prev.slice(lineStart);
      });
      setSelection((s) => ({ start: s.start + prefix.length, end: s.end + prefix.length }));
      setSaveState('unsaved');
    },
    [selection],
  );

  const handleTitleChange = useCallback((value: string) => {
    setTitle(value);
    setSaveState('unsaved');
  }, []);

  const handleBodyChange = useCallback((value: string) => {
    setBody(value);
    setSaveState('unsaved');
  }, []);

  const flush = useCallback(
    (id: NoteId) => {
      const titleChanged = title !== note.title;
      const bodyChanged = body !== note.body;
      if (!titleChanged && !bodyChanged) {
        setSaveState('idle');
        return;
      }
      const partial: Partial<Note> = {
        ...(titleChanged ? { title } : {}),
        ...(bodyChanged ? { body } : {}),
      };
      setSaveState('saving');
      void (async () => {
        try {
          await onSave(id, partial);
          log.debug('notes.editor.saved');
          setSaveState('saved');
        } catch (error) {
          log.error('notes.editor.failed', error as Error);
          setSaveState('unsaved');
        }
      })();
    },
    [title, body, note.title, note.body, onSave],
  );

  const handleBlur = useCallback(() => {
    flush(note.id);
  }, [flush, note.id]);

  const statusLabel =
    saveState === 'saving'
      ? notesStrings.editor.saving
      : saveState === 'saved'
        ? notesStrings.editor.saved
        : saveState === 'unsaved'
          ? notesStrings.editor.unsaved
          : '';

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        {onClose !== undefined ? (
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={notesStrings.doneCta}
            style={({ pressed }) => [pressed && { opacity: 0.6 }]}
          >
            <Text style={[styles.headerAction, { color: theme.colors.accent.indigo }]}>
              {notesStrings.doneCta}
            </Text>
          </Pressable>
        ) : (
          <View />
        )}
        <Text style={[styles.status, { color: theme.colors.text.tertiary }]}>{statusLabel}</Text>
      </View>
      <TextInput
        value={title}
        onChangeText={handleTitleChange}
        onBlur={handleBlur}
        placeholder={notesStrings.noteTitlePlaceholder}
        placeholderTextColor={theme.colors.text.tertiary}
        accessibilityLabel={notesStrings.noteTitleLabel}
        style={[styles.titleInput, { color: theme.colors.text.primary }]}
      />
      {/* Markdown formatting toolbar (Figma editor chrome). Each button
          wraps the selection / prefixes the line with a markdown marker;
          the body persists as markdown. className-first; icon color is
          inline since lucide icons don't inherit text-* classes. */}
      <View className="flex flex-row items-center gap-1 px-3 py-1.5">
        {(
          [
            { key: 'bold', Icon: Bold, run: () => wrapSelection('**'), label: 'Bold' },
            { key: 'italic', Icon: Italic, run: () => wrapSelection('_'), label: 'Italic' },
            { key: 'strike', Icon: Strikethrough, run: () => wrapSelection('~~'), label: 'Strikethrough' },
            { key: 'code', Icon: Code, run: () => wrapSelection('`'), label: 'Inline code' },
            { key: 'list', Icon: List, run: () => prefixLine('- '), label: 'Bullet list' },
            { key: 'quote', Icon: Quote, run: () => prefixLine('> '), label: 'Quote' },
          ] as const
        ).map(({ key, Icon, run, label }) => (
          <Pressable
            key={key}
            onPress={run}
            accessibilityRole="button"
            accessibilityLabel={label}
            className="w-8 h-8 rounded-md items-center justify-center hover:bg-light-surface-secondary dark:hover:bg-white/[0.06]"
          >
            <Icon size={16} strokeWidth={2} color={theme.colors.text.secondary} />
          </Pressable>
        ))}
      </View>
      <TextInput
        value={body}
        onChangeText={handleBodyChange}
        onBlur={handleBlur}
        selection={selection}
        onSelectionChange={(e) => setSelection(e.nativeEvent.selection)}
        placeholder={notesStrings.noteBodyPlaceholder}
        placeholderTextColor={theme.colors.text.tertiary}
        accessibilityLabel={notesStrings.noteBodyLabel}
        multiline
        textAlignVertical="top"
        style={[styles.bodyInput, { color: theme.colors.text.primary }]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  headerAction: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  status: {
    fontSize: 12,
    fontWeight: '500',
  } satisfies TextStyle,
  titleInput: {
    fontSize: 22,
    fontWeight: '700',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  } satisfies TextStyle,
  bodyInput: {
    flex: 1,
    fontSize: 15,
    lineHeight: 22,
    paddingHorizontal: 16,
    paddingBottom: 16,
  } satisfies TextStyle,
});
